import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { SERVICES, SectionHeader } from "../components/Services";
import FinalCTA from "../components/FinalCTA";

const DETAILS = {
    "CATI Surveys": [
        "Multi-language CATI fieldwork (B2B/B2C)",
        "Live supervisor consoles & call recording",
        "Soft and hard quotas with real-time dashboards",
    ],
    "Customer Satisfaction": [
        "NPS, CSAT, CES — structured & longitudinal",
        "Driver analysis & key-driver heatmaps",
        "Closed-loop alerts to ops or sales",
    ],
    "Healthcare Research": [
        "Patient experience & adherence studies",
        "HCP and KOL interviews",
        "HIPAA-aware data handling",
    ],
    "B2B / B2C Research": [
        "Decision-maker interviews and panels",
        "Profile validation, screeners & quotas",
        "Cross-market comparable studies",
    ],
    "Data Collection": [
        "CATI, online & hybrid modes",
        "Custom screeners with smart routing",
        "Multi-region scaling without quality loss",
    ],
    "Data Validation": [
        "Voice audits & logic-rule checks",
        "Fraud scoring + duplicate detection",
        "Independent human verification layer",
    ],
    "Reporting & Insights": [
        "Topline & detailed cross-tab decks",
        "Live dashboards (web + mobile)",
        "Story-led recommendations, not chart dumps",
    ],
};

export default function Services() {
    return (
        <main className="pt-32 pb-0" data-testid="services-page">
            <section className="relative pb-12">
                <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30 pointer-events-none" />
                <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-blue-500/15 rounded-full blur-3xl pointer-events-none" />
                <div className="pr-container relative">
                    <SectionHeader
                        overline="Services"
                        title="Composable capabilities, end-to-end research"
                        subtitle="From scripting to dashboards — Proksy delivers the full pipeline of human signal, validated and ready to act on."
                    />
                </div>
            </section>

            <section className="py-12">
                <div className="pr-container space-y-5">
                    {SERVICES.map((s, i) => {
                        const Icon = s.icon;
                        const isBlue = s.accent === "blue";
                        return (
                            <motion.div
                                key={s.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.5, delay: i * 0.04 }}
                                className="pr-card rounded-2xl p-7 sm:p-8 grid lg:grid-cols-12 gap-6 items-start"
                                data-testid={`service-detail-${i}`}
                            >
                                <div className="lg:col-span-3 flex items-center gap-4">
                                    <div
                                        className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                                            isBlue
                                                ? "bg-blue-500/10 border border-blue-400/30"
                                                : "bg-emerald-500/10 border border-emerald-400/30"
                                        }`}
                                    >
                                        <Icon
                                            className={`w-6 h-6 ${
                                                isBlue
                                                    ? "text-blue-300"
                                                    : "text-emerald-300"
                                            }`}
                                        />
                                    </div>
                                    <div>
                                        <span
                                            className={`block font-mono-pr text-[10px] uppercase tracking-[0.2em] ${
                                                isBlue
                                                    ? "text-blue-300"
                                                    : "text-emerald-300"
                                            }`}
                                        >
                                            0{i + 1}
                                        </span>
                                        <h3 className="font-outfit text-xl font-semibold text-white">
                                            {s.title}
                                        </h3>
                                    </div>
                                </div>

                                <div className="lg:col-span-5 text-sm text-slate-300 leading-relaxed">
                                    {s.desc}
                                </div>

                                <div className="lg:col-span-4">
                                    <ul className="space-y-2.5 text-sm text-slate-300">
                                        {(DETAILS[s.title] || []).map((d) => (
                                            <li
                                                key={d}
                                                className="flex items-start gap-2.5"
                                            >
                                                <CheckCircle2
                                                    className={`w-4 h-4 mt-0.5 ${
                                                        isBlue
                                                            ? "text-blue-300"
                                                            : "text-emerald-300"
                                                    }`}
                                                />
                                                <span>{d}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </motion.div>
                        );
                    })}

                    <div className="pt-4">
                        <Link
                            to="/contact"
                            className="btn-primary"
                            data-testid="services-page-cta"
                        >
                            Discuss your study
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            <FinalCTA />
        </main>
    );
}
